import { useEffect } from 'react';
import { BRAND_COLORS, SUBJECT_THEME } from '@lk/shared';
import { cn } from '../../lib/cn';
import { CoinCounter } from '../../components/CoinCounter';
import { Panel } from '../../components/Panel';
import { Button } from '../../components/Button';
import { Icon } from '../../components/Icon';
import { playSound } from '../../audio/sound';
import { humanMessageForCode } from '../../lib/errors';
import { useSyncState } from '../../offline/useSync';
import type { QueuedAttempt } from '../../offline/db';
import { coinReasonLabel } from './coins';
import type { QuizItem } from './types';

export interface FeedbackProps {
  item: QuizItem;
  attempt: QueuedAttempt | undefined;
  onNext: () => void;
  isLast: boolean;
}

/**
 * Екран після відповіді.
 *
 * Три принципові рішення:
 *  1) Пояснення показуємо ЗАВЖДИ — і на помилці, і на правильній відповіді.
 *     Саме воно, а не монета, є головною навчальною цінністю.
 *  2) Поки сервер не підтвердив спробу, ми НЕ вигадуємо «правильно/неправильно»
 *     і не пишемо «збережено на сервері». Чесно: «збережено на пристрої,
 *     перевіримо, щойно буде звʼязок».
 *  3) Станів рівно ТРИ, а не два. «Не зараховано» — це не різновид
 *     «ще чекаємо»: у першому випадку балів не буде ніколи, і сказати про це
 *     треба одразу й на дитячій мові, разом із причиною. Доки станів було
 *     два, незарахована відповідь роками показувалася як «перевіряємо…».
 */
export function Feedback({ item, attempt, onNext, isLast }: FeedbackProps): React.JSX.Element {
  const sync = useSyncState();
  const result = attempt?.result ?? null;
  const theme = SUBJECT_THEME[item.subject];

  useEffect(() => {
    if (!result) return;
    playSound(result.isCorrect ? 'correct' : 'wrong');
    if (result.masteryAfter > result.masteryBefore + 0.12) playSound('levelUp');
  }, [result]);

  const rejected = attempt?.status === 'rejected';
  // Причину бере сервер, мову — ми: `message` теж українською, але дитині
  // краще читати наш текст, який знає екран і час.
  const rejectionReason = attempt?.rejection
    ? humanMessageForCode(attempt.rejection.code, attempt.rejection.message)
    : (attempt?.lastError ?? '');

  return (
    <div className="flex flex-col gap-4" data-testid="feedback">
      {result ? (
        <Panel
          className={cn('overflow-hidden p-5 text-center', !result.isCorrect && 'lk-anim-shake')}
          style={{
            borderColor: result.isCorrect ? BRAND_COLORS.green : BRAND_COLORS.red,
          }}
          data-correct={result.isCorrect}
        >
          <Icon
            name={result.isCorrect ? 'correct' : 'wrong'}
            size={96}
            label={result.isCorrect ? 'Правильна відповідь' : 'Неправильна відповідь'}
            className={cn('mx-auto', result.isCorrect && 'lk-anim-pop')}
          />
          <h2
            className="mt-1 text-3xl font-black"
            style={{ color: result.isCorrect ? BRAND_COLORS.green : BRAND_COLORS.red }}
          >
            {result.isCorrect ? 'Правильно!' : 'Не влучив'}
          </h2>

          {!result.isCorrect ? (
            <p className="mt-2 text-sm font-bold text-white/70">
              Правильна відповідь:{' '}
              <span className="text-white">{result.correctAnswer}</span>
            </p>
          ) : null}

          {result.coinsAwarded > 0 ? (
            <div className="mt-3 flex items-center justify-center gap-2">
              <Icon name="coin" size={32} />
              <CoinCounter
                value={result.coinsAwarded}
                withSound
                prefix={<span className="text-[var(--lk-gold)]">+</span>}
                className="text-4xl text-[var(--lk-gold)]"
                suffix={<span className="text-base text-white/60">грн</span>}
              />
            </div>
          ) : (
            <p className="mt-3 text-sm font-bold text-white/50">Балів цього разу немає</p>
          )}

          {result.coinReasons.length > 0 ? (
            <ul className="mt-2 flex flex-wrap justify-center gap-1.5">
              {result.coinReasons.map((reason) => (
                <li key={reason} className="lk-chip text-[10px]">
                  {coinReasonLabel(reason)}
                </li>
              ))}
            </ul>
          ) : null}

          {result.streak > 1 ? (
            <p className="mt-2 flex items-center justify-center gap-1.5 text-sm font-black text-[var(--lk-gold)]">
              <Icon name="streak" size={18} />
              Серія {result.streak} поспіль
            </p>
          ) : null}
        </Panel>
      ) : rejected ? (
        <Panel
          className="p-5 text-center"
          style={{ borderColor: BRAND_COLORS.red }}
          data-testid="feedback-rejected"
        >
          <Icon name="offline" size={80} label="Відповідь не зарахована" className="mx-auto" />
          <h2 className="mt-2 text-2xl font-black" style={{ color: BRAND_COLORS.red }}>
            Не зараховано
          </h2>
          <p className="mt-2 text-sm font-semibold text-white/70">{rejectionReason}</p>
          {/*
            Головне для дитини — не «сервер відмовив», а що робити далі.
            Відповідь нікуди не поділася, і дорослий може повернути її в чергу.
          */}
          <p className="mt-2 text-xs font-semibold text-white/50">
            Балів за неї не буде. Ми її зберегли — покажи батькам, вони зможуть надіслати ще раз.
          </p>
        </Panel>
      ) : (
        <Panel className="p-5 text-center" data-testid="feedback-pending">
          <Icon
            name={sync.online ? 'sync' : 'offline'}
            size={80}
            label={sync.online ? 'Перевіряємо на сервері' : 'Чекає на інтернет'}
            className="lk-anim-float mx-auto"
          />
          <h2 className="mt-2 text-2xl font-black">Відповідь збережено</h2>
          <p className="mt-2 text-sm font-semibold text-white/70">
            {sync.online
              ? 'Перевіряємо на сервері…'
              : 'Інтернету немає — відповідь лежить у тебе на пристрої й піде на сервер автоматично. Грай далі, нічого не загубиться.'}
          </p>
        </Panel>
      )}

      {/* Пояснення — головна цінність. Показуємо, щойно воно є. */}
      {result?.explanation ? (
        <Panel tight className="p-4" style={{ borderColor: `${theme.color}66` }}>
          <p className="text-[11px] font-black tracking-widest text-white/45 uppercase">
            Чому так
          </p>
          <p className="mt-1 text-sm leading-relaxed font-semibold text-white/85">
            {result.explanation}
          </p>
        </Panel>
      ) : null}

      {/*
        Шкали опанування («Навичка · X · 25% → 31%») тут навмисно НЕМАЄ.
        Це службова метрика моделі учня: дитині вона нічого не пояснює,
        зате перетворює розбір помилки на табло з відсотками.
        Місце цих даних — теплова мапа навичок у кабінеті батьків (/parent/skills).
        Сам сигнал не втрачено: помітний стрибок опанування досі озвучується.
      */}

      {result && result.unlockedAchievements.length > 0 ? (
        <Panel tight className="border-[var(--lk-gold)]/60 p-4">
          <p className="text-[11px] font-black tracking-widest text-white/45 uppercase">
            Нове досягнення
          </p>
          <p className="mt-1 flex items-center gap-2 text-base font-black text-[var(--lk-gold)]">
            <Icon name="medal" size={22} />
            <span className="min-w-0 flex-1">{result.unlockedAchievements.join(', ')}</span>
          </p>
        </Panel>
      ) : null}

      <Button tone="gold" size="xl" block onClick={onNext} data-testid="next-question">
        {isLast ? 'Підсумок' : 'Далі →'}
      </Button>
    </div>
  );
}
